import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { s25Resource } from "../../../util/s25.resource";
import { S25LoadingInlineStaticComponent } from "../../s25-loading/s25.loading.inline.static.component";
import { UserStore } from "../../../stores/user.store";
import { ExperienceListItem, TrailApi } from "./trail.api";
import { TrailLabels } from "./trail.labels";
import { formatUserprefDate } from "./trail.userpref.date";

type StatusFilter = "" | "PENDING" | "APPROVED" | "DENIED";

@Component({
    selector: "s25-ng-trail-experience-list",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, FormsModule, S25LoadingInlineStaticComponent],
    template: `
        <div class="trail-experience-list">
            <div class="trail-experience-list__filter">
                <label for="exp-status-filter">Status:</label>
                <select
                    id="exp-status-filter"
                    class="c-input trail-experience-list__select"
                    [ngModel]="statusFilter()"
                    (ngModelChange)="statusFilter.set($event)"
                >
                    <option value="">All</option>
                    <option value="PENDING">{{ labels.APPROVAL_PENDING }}</option>
                    <option value="APPROVED">{{ labels.APPROVED }}</option>
                    <option value="DENIED">{{ labels.DENIED }}</option>
                </select>
            </div>

            @if (experiencesRes.isLoading()) {
                <s25-ng-loading-inline-static [text]="'Loading experiences…'" />
            } @else if (experiencesRes.error()) {
                <p class="trail-experience-list__error">Unable to load experiences.</p>
            } @else if (!experiences().length) {
                <p class="trail-experience-list__empty">{{ labels.NO_EXPERIENCES }}</p>
            } @else {
                <div class="trail-experience-list__cards">
                    @for (experience of experiences(); track experience.experienceId) {
                        <div
                            class="trail-experience-list__card"
                            [class.trail-experience-list__card--pending]="experience.status === 'PENDING'"
                            [class.trail-experience-list__card--approved]="experience.status === 'APPROVED'"
                            [class.trail-experience-list__card--denied]="experience.status === 'DENIED'"
                        >
                            <div class="trail-experience-list__card-header">
                                <span class="trail-experience-list__card-title">{{ experience.title }}</span>
                                <span
                                    class="trail-experience-list__status-badge"
                                    [class.trail-experience-list__status-badge--pending]="
                                        experience.status === 'PENDING'
                                    "
                                    [class.trail-experience-list__status-badge--approved]="
                                        experience.status === 'APPROVED'
                                    "
                                    [class.trail-experience-list__status-badge--denied]="experience.status === 'DENIED'"
                                >
                                    {{ statusLabel(experience.status) }}
                                </span>
                            </div>

                            @if (experience.curriculumName || experience.trailName) {
                                <div class="trail-experience-list__meta">
                                    @if (experience.curriculumName) {
                                        <span>{{ experience.curriculumName }}</span>
                                    }
                                    @if (experience.curriculumName && experience.trailName) {
                                        <span> · </span>
                                    }
                                    @if (experience.trailName) {
                                        <span>{{ experience.trailName }}</span>
                                    }
                                </div>
                            }

                            <div class="trail-experience-list__date">
                                Experience date: {{ formatDate(experience.experienceDt) }}
                            </div>

                            <p class="trail-experience-list__description">{{ experience.description }}</p>

                            @if (experience.status === "APPROVED") {
                                <div class="trail-experience-list__review">
                                    @if (experience.approvalReason) {
                                        <p class="trail-experience-list__reason">
                                            <strong>{{ labels.APPROVAL_REASON }}:</strong>
                                            {{ experience.approvalReason }}
                                        </p>
                                    }
                                    @if (experience.reviewerName) {
                                        <p class="trail-experience-list__reviewer">
                                            Reviewed by {{ experience.reviewerName }}
                                        </p>
                                    }
                                </div>
                            }

                            @if (experience.status === "DENIED") {
                                <div class="trail-experience-list__review trail-experience-list__review--denied">
                                    @if (experience.denialReason) {
                                        <p class="trail-experience-list__reason">
                                            <strong>{{ labels.DENIAL_REASON }}:</strong> {{ experience.denialReason }}
                                        </p>
                                    }
                                    @if (experience.reviewerName) {
                                        <p class="trail-experience-list__reviewer">
                                            Reviewed by {{ experience.reviewerName }}
                                        </p>
                                    }
                                </div>
                            }
                        </div>
                    }
                </div>
            }
        </div>
    `,
    styles: `
        :host {
            display: block;
        }
        .trail-experience-list__filter {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            margin-bottom: 1rem;
        }
        .trail-experience-list__select {
            min-width: 10rem;
        }
        .trail-experience-list__error {
            color: var(--color--danger);
        }
        .trail-experience-list__empty {
            color: var(--color--text-muted);
            font-style: italic;
        }
        .trail-experience-list__cards {
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
        }
        .trail-experience-list__card {
            border: 1px solid var(--color--border);
            border-left: 4px solid var(--color--border);
            border-radius: 8px;
            padding: 0.75rem 1rem;
            background: var(--color--surface-2-bg);
        }
        .trail-experience-list__card--pending {
            border-left-color: #f0ad4e;
        }
        .trail-experience-list__card--approved {
            border-left-color: #28a745;
        }
        .trail-experience-list__card--denied {
            border-left-color: #dc3545;
            background: #fff5f5;
        }
        .trail-experience-list__card-header {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            flex-wrap: wrap;
            margin-bottom: 0.35rem;
        }
        .trail-experience-list__card-title {
            font-weight: 600;
            font-size: 0.95rem;
        }
        .trail-experience-list__status-badge {
            display: inline-block;
            padding: 0.15rem 0.5rem;
            border-radius: 4px;
            font-size: 0.75rem;
            font-weight: 600;
            text-transform: uppercase;
        }
        .trail-experience-list__status-badge--pending {
            background: #fff3cd;
            color: #856404;
        }
        .trail-experience-list__status-badge--approved {
            background: #d4edda;
            color: #155724;
        }
        .trail-experience-list__status-badge--denied {
            background: #f8d7da;
            color: #721c24;
        }
        .trail-experience-list__meta {
            font-size: 0.85rem;
            color: var(--color--text-muted);
            margin-bottom: 0.25rem;
        }
        .trail-experience-list__date {
            font-size: 0.82rem;
            color: var(--color--text-muted);
            margin-bottom: 0.5rem;
        }
        .trail-experience-list__description {
            margin: 0 0 0.5rem;
            font-size: 0.9rem;
            line-height: 1.4;
        }
        .trail-experience-list__review {
            padding: 0.5rem 0.75rem;
            background: #f0faf0;
            border-radius: 4px;
            font-size: 0.85rem;
        }
        .trail-experience-list__review--denied {
            background: #fef0f0;
        }
        .trail-experience-list__reason {
            margin: 0 0 0.25rem;
        }
        .trail-experience-list__reviewer {
            margin: 0;
            font-size: 0.8rem;
            color: var(--color--text-muted);
        }
    `,
})
export class TrailExperienceListComponent {
    private readonly userStore = inject(UserStore);

    formatDate(value: string | undefined | null): string {
        return formatUserprefDate(value, this.userStore.dateFormat());
    }

    readonly reloadToken = input(0);
    readonly labels = TrailLabels;
    readonly statusFilter = signal<StatusFilter>("");

    readonly experiencesRes = s25Resource({
        params: () => ({ status: this.statusFilter(), reload: this.reloadToken() }),
        loader: ({ params }) => TrailApi.getMyExperiences(params.status === "" ? undefined : params.status),
    });

    readonly experiences = computed((): ExperienceListItem[] => this.experiencesRes.value() ?? []);

    statusLabel(status: string): string {
        switch (status) {
            case "PENDING":
                return TrailLabels.APPROVAL_PENDING;
            case "APPROVED":
                return TrailLabels.APPROVED;
            case "DENIED":
                return TrailLabels.DENIED;
            default:
                return status;
        }
    }
}
